import React, { useEffect, useMemo, useState } from 'react';
import { HugeiconsIcon } from '@hugeicons/react';
import { Alert01Icon, Clock01Icon } from '@hugeicons/core-free-icons';
import { isErrorInfo } from '../../../shared/interfaces/error-info.interface';
import { LeadSlaPolicyModel, LeadSlaPolicyUpdateModel } from '../../../shared/models/lead.model';
import { getLeadSlaPolicy, updateLeadSlaPolicy } from '../../../shared/services/data/lead.data';
import { birdToast } from '../../../shared/components/core-ui/toast';
import {
  DurationUnit,
  humaniseTarget,
  ratioError,
  targetError,
  toMinutes,
  toUnit,
  unitFor
} from '../../shared/sla-duration.util';

const CONTENT_CLASS = 'max-w-[1440px] mx-auto px-[clamp(16px,3vw,48px)]';
const SECTION_CLASS = 'py-[40px]';
const LABEL_CLASS = 'flex flex-col gap-[6px] text-[12px] font-gilroy-medium text-neutral-300';
const INPUT_CLASS =
  'h-[40px] w-full rounded-[10px] border border-gray-200 bg-neutral-0 px-[12px] text-[13px] text-neutral-500 hover:border-neutral-500 focus:border-neutral-500 outline-none transition-colors';

/** Spelled out rather than title-cased: "Reservation" is the funnel, not one booking. */
const VERTICAL_LABEL: Record<string, string> = {
  reservation: 'Reservations',
  furnishing: 'Furnishing',
  acquisition: 'Acquisition'
};

const verticalLabel = (vertical: string): string =>
  VERTICAL_LABEL[vertical] ?? vertical.replace(/_/g, ' ');

/** The draft a row is being edited as — a number and the unit it was typed in. */
interface DraftTarget {
  value: string;
  unit: DurationUnit;
}

const draftFromMinutes = (minutes: number): DraftTarget => {
  const unit: DurationUnit = unitFor(minutes);
  return { value: String(toUnit(minutes, unit)), unit };
};

const minutesOf = (draft: DraftTarget): number => toMinutes(Number(draft.value), draft.unit);

/**
 * Speed-to-lead targets, editable.
 *
 * These numbers decide whether the whole lead queue reads red. Until this
 * screen existed they were a constant in `server.service.lead` and changing
 * one meant a deploy, which is how furnishing sat at an unreachable hour long
 * enough for the badge to stop meaning anything.
 *
 * **A change applies to new leads only.** `slaDueAt` is stamped when a lead
 * arrives and every report reads the stored value, so raising a target does
 * not retroactively forgive the breaches already in the queue — which is the
 * point: historic SLA reporting stays comparable across a policy change. The
 * screen says so, because "why is the queue still red" is the first question
 * anyone asks after saving.
 */
export const LeadSlaConfigContainer: React.FC = () => {
  const [policy, setPolicy] = useState<LeadSlaPolicyModel | undefined>();
  const [targets, setTargets] = useState<Record<string, DraftTarget>>({});
  const [percent, setPercent] = useState<string>('');
  const [loadFailed, setLoadFailed] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);

  const apply = (next: LeadSlaPolicyModel): void => {
    setPolicy(next);
    setTargets(
      Object.keys(next.targetMinutes).reduce(
        (acc: Record<string, DraftTarget>, vertical: string) => {
          acc[vertical] = draftFromMinutes(next.targetMinutes[vertical]);
          return acc;
        },
        {}
      )
    );
    setPercent(String(Math.round(next.atRiskRatio * 100)));
  };

  useEffect(() => {
    getLeadSlaPolicy().then((result) => {
      if (isErrorInfo(result)) {
        setLoadFailed(true);
        return;
      }
      apply(result);
    });
  }, []);

  const errors: Record<string, string | undefined> = useMemo(() => {
    const next: Record<string, string | undefined> = {};
    Object.keys(targets).forEach((vertical: string) => {
      next[vertical] = targets[vertical].value.trim()
        ? targetError(minutesOf(targets[vertical]))
        : 'Enter a target';
    });
    next.ratio = percent.trim() ? ratioError(Number(percent)) : 'Enter a threshold';
    return next;
  }, [targets, percent]);

  const hasErrors: boolean = Object.keys(errors).some((key: string) => Boolean(errors[key]));

  /**
   * Only what actually changed.
   *
   * Sending the whole policy on every save would mean one person's open tab
   * overwriting another's edit to a vertical they never touched.
   */
  const changes: LeadSlaPolicyUpdateModel = useMemo(() => {
    if (!policy) return {};

    const targetMinutes: Record<string, number> = {};
    Object.keys(targets).forEach((vertical: string) => {
      const minutes: number = minutesOf(targets[vertical]);
      if (minutes !== policy.targetMinutes[vertical]) targetMinutes[vertical] = minutes;
    });

    const ratio: number = Number(percent) / 100;
    const next: LeadSlaPolicyUpdateModel = {};
    if (Object.keys(targetMinutes).length) next.targetMinutes = targetMinutes;
    if (Math.abs(ratio - policy.atRiskRatio) > 0.0001) next.atRiskRatio = ratio;
    return next;
  }, [policy, targets, percent]);

  const isDirty: boolean = Object.keys(changes).length > 0;

  const save = (payload: LeadSlaPolicyUpdateModel, message: string): void => {
    setSaving(true);
    updateLeadSlaPolicy(payload)
      .then((result) => {
        if (isErrorInfo(result)) {
          birdToast.error(result.message || 'Could not save the SLA policy');
          return;
        }
        apply(result);
        birdToast.success(message);
      })
      .finally(() => setSaving(false));
  };

  /**
   * Clears every override, which is not the same as typing the old numbers in.
   *
   * An override set to the same value as the default still pins it: a later
   * change to the service's default would not reach it. Sending `null` per
   * vertical puts the policy back to following the service.
   */
  const restoreDefaults = (): void => {
    const targetMinutes: Record<string, number | null> = {};
    Object.keys(targets).forEach((vertical: string) => {
      targetMinutes[vertical] = null;
    });
    save({ targetMinutes, atRiskRatio: null }, 'Restored the built-in targets');
  };

  if (loadFailed) {
    return (
      <div className="min-h-[calc(100vh-45px)] bg-neutral-0 font-gilroy-regular text-neutral-500">
        <div className={`${CONTENT_CLASS} pt-[32px]`}>
          <div className="rounded-[16px] bg-creams-100 px-[24px] py-[32px] text-center text-[15px] text-neutral-300">
            Could not load the SLA policy. The lead service may be unavailable — targets are still
            being applied from its built-in defaults.
          </div>
        </div>
      </div>
    );
  }

  const verticals: string[] = Object.keys(targets);

  return (
    <div className="min-h-[calc(100vh-45px)] bg-neutral-0 font-gilroy-regular text-neutral-500">
      <div className={`${CONTENT_CLASS} pt-[32px] pb-[24px]`}>
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-[16px]">
          <div className="flex items-center gap-[10px] flex-wrap">
            <h1 className="m-0 text-[clamp(26px,2.6vw,40px)] leading-tight font-gilroy-bold text-neutral-500">
              Response targets
            </h1>
            {policy && !policy.isCustomised && (
              <span className="inline-flex items-center gap-[4px] px-[12px] py-[4px] rounded-full text-[13px] font-gilroy-medium whitespace-nowrap bg-creams-100 border border-gray-200 text-neutral-500">
                Built-in defaults
              </span>
            )}
          </div>

          <div className="flex items-center gap-[6px] flex-wrap shrink-0">
            {policy && policy.isCustomised && (
              <button
                type="button"
                onClick={restoreDefaults}
                disabled={saving}
                className="h-[44px] inline-flex items-center gap-[6px] px-[16px] rounded-[12px] border border-gray-200 bg-neutral-0 text-[14px] font-gilroy-medium text-neutral-500 hover:border-neutral-500 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
              >
                Restore defaults
              </button>
            )}
            <button
              type="button"
              onClick={() => save(changes, 'Targets saved')}
              disabled={!isDirty || hasErrors || saving}
              className="h-[44px] inline-flex items-center gap-[6px] px-[16px] rounded-[12px] bg-primary-500 text-neutral-0 text-[14px] font-gilroy-medium hover:bg-primary-600 cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </div>
      </div>

      <div className={`${CONTENT_CLASS} pb-[80px]`}>
        <div className="divide-y divide-gray-200">
          <section className={SECTION_CLASS}>
            <h2 className="m-0 mb-[8px] text-[clamp(18px,1.5vw,22px)] font-gilroy-bold text-neutral-500">
              Time to first response
            </h2>

            {/*
              Said once, at the top, rather than next to each field: it is the
              single thing that surprises people about this screen, and
              repeating it per row would be three copies of the same sentence.
            */}
            <p className="m-0 mb-[24px] flex items-start gap-[8px] text-[13px] text-neutral-300">
              <span className="mt-[2px] shrink-0 text-neutral-300" aria-hidden="true">
                <HugeiconsIcon icon={Alert01Icon} size={16} strokeWidth={1.5} />
              </span>
              A change applies to leads created afterwards. Leads already in the queue keep the
              target they arrived under, so past SLA reporting stays comparable.
            </p>

            <div className="grid grid-cols-1 gap-[20px] md:grid-cols-3">
              {verticals.map((vertical: string) => (
                <label key={vertical} className={LABEL_CLASS}>
                  {verticalLabel(vertical)}
                  <div className="flex items-stretch gap-[8px]">
                    <input
                      type="number"
                      min={1}
                      value={targets[vertical].value}
                      aria-label={`${verticalLabel(vertical)} target`}
                      onChange={(event) => {
                        // Read before the updater runs. React 17 pools
                        // synthetic events, so `event.target` is recycled by
                        // the time a functional setState callback is called —
                        // the field silently refuses to change.
                        const value: string = event.target.value;
                        setTargets((current) => ({
                          ...current,
                          [vertical]: { ...current[vertical], value }
                        }));
                      }}
                      className={INPUT_CLASS}
                    />
                    {/*
                      A two-state toggle rather than a dropdown. There are two
                      options and a react-select here would be a menu to open
                      for a choice that is visible as a pair of buttons.
                    */}
                    <div
                      className="inline-flex h-[40px] shrink-0 overflow-hidden rounded-[10px] border border-gray-200"
                      role="group"
                      aria-label={`${verticalLabel(vertical)} unit`}
                    >
                      {(['minutes', 'hours'] as DurationUnit[]).map((unit: DurationUnit) => (
                        <button
                          key={unit}
                          type="button"
                          aria-pressed={targets[vertical].unit === unit}
                          onClick={() =>
                            setTargets((current) => {
                              // Convert rather than reinterpret: switching the
                              // unit must not silently turn 15 minutes into
                              // 15 hours under somebody's cursor.
                              const minutes: number = minutesOf(current[vertical]);
                              return {
                                ...current,
                                [vertical]: { value: String(toUnit(minutes, unit)), unit }
                              };
                            })
                          }
                          className={`px-[12px] text-[13px] font-gilroy-medium transition-colors ${
                            targets[vertical].unit === unit
                              ? 'bg-creams-200 text-neutral-500'
                              : 'bg-neutral-0 text-neutral-300 hover:text-neutral-500'
                          }`}
                        >
                          {unit === 'minutes' ? 'min' : 'hrs'}
                        </button>
                      ))}
                    </div>
                  </div>

                  {errors[vertical] ? (
                    <span className="text-[12px] font-gilroy-medium text-secondary-700">
                      {errors[vertical]}
                    </span>
                  ) : (
                    <span className="flex items-center gap-[4px] text-[12px] text-neutral-300">
                      <HugeiconsIcon icon={Clock01Icon} size={14} strokeWidth={1.5} />
                      {humaniseTarget(minutesOf(targets[vertical]))}
                    </span>
                  )}
                </label>
              ))}
            </div>
          </section>

          <section className={SECTION_CLASS}>
            <h2 className="m-0 mb-[24px] text-[clamp(18px,1.5vw,22px)] font-gilroy-bold text-neutral-500">
              At-risk threshold
            </h2>

            <div className="grid grid-cols-1 gap-[20px] md:grid-cols-3">
              <label className={LABEL_CLASS}>
                Warn at
                <div className="flex items-center gap-[8px]">
                  <input
                    type="number"
                    min={10}
                    max={99}
                    value={percent}
                    aria-label="At-risk threshold"
                    onChange={(event) => setPercent(event.target.value)}
                    className={INPUT_CLASS}
                  />
                  <span className="text-[14px] text-neutral-300">%</span>
                </div>
                {errors.ratio ? (
                  <span className="text-[12px] font-gilroy-medium text-secondary-700">
                    {errors.ratio}
                  </span>
                ) : (
                  <span className="text-[12px] text-neutral-300">
                    A furnishing lead turns amber after{' '}
                    {humaniseTarget(
                      Math.round(
                        minutesOf(targets.furnishing ?? { value: '0', unit: 'minutes' }) *
                          (Number(percent) / 100)
                      )
                    )}
                  </span>
                )}
              </label>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};
